import React from 'react'
import View from '../../atoms/view'
import { SidebarIcon } from '../../atoms/sideBarIcon'
import Button from '@modul-connect/shared/components/atoms/button'

export const Sidebar = ({
  children,
  variant = 'old',
  open = true,
  showFilterBar = () => false,
  disturbanceBannerHeight = 0,
  themes,
}) => {
  if (!open) return null
  const S = styles(showFilterBar, disturbanceBannerHeight)

  return (
    <View extend={S.sidebar_container}>
      <View extend={S.outer_sidebar}>
        {React.Children.map(children, (child) => {
          return child
        })}
      </View>
    </View>
  )
}

/* ─────────────────────────── Subcomponents ─────────────────────────── */

const TOKENS = {
  old: {
    panelBg: '#fff',
    railBg: '#f5f5f5',
    divider: '#000',
    headerText: '#404040',
    sectionText: '#6B6B6B',
    itemText: '#4D4D4D',
    itemRadius: 4,
    link: '#0070f3',
    panelBorder: '#BCBCBC',
  },
  new: {
    panelBg: '#FFFFFF',
    railBg: '#F6F7F8',
    divider: '#D8D8D8',
    headerText: '#4D4D4D',
    sectionText: '#4d4d4d',
    itemText: '#4D4D4D',
    itemRadius: 6,
    link: '#1744E2',
    panelBorder: '#D8D8D8',
  },
}

Sidebar.Rail = function Rail({ children }) {
  const S = railPanelStyles()
  return <View extend={S.rail}>{children}</View>
}

Sidebar.Panel = function Panel({ children, variant = 'old' }) {
  const S = railPanelStyles(variant)
  return <View extend={S.panel}>{children}</View>
}

Sidebar.Header = function Header({ icon, title, variant = 'old' }) {
  const T = TOKENS[variant]
  const isStringIcon = typeof icon === 'string'

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: 56,
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
        borderBottom: `1px solid ${T.divider}`,
        paddingRight: 4,
        position: 'sticky',
        top: 0,
        background: T.panelBg,
        marginBottom: 24
      }}
    >
      {icon &&
        (isStringIcon ? (
          <SidebarIcon icon={icon} dark size={16} />
        ) : (
          icon
        ))}
      <span
        style={{
          fontFamily: 'Inter',
          fontWeight: 400,
          lineHeight: '20px',
          letterSpacing: '0.04em',
          color: T.headerText,
        }}
      >
        {title}
      </span>
    </View>
  )
}

Sidebar.Container = function Container({ children, style, ...rest }) {
  return(
    <div
      {...rest}
      style={{
        marginBottom: 20,
        width: '100%',
        ...style,
      }}>
       
      {children}

    </div>
  )
}

Sidebar.Subtitle = function Subtitle({ children, variant = 'old' }) {
  const T = TOKENS[variant]
  return (
    <div
      style={{
        fontFamily: 'Inter',
        fontWeight: 300,
        fontSize: 14,
        marginBottom: 10,
        color: T.sectionText,
   
        marginLeft: 4,
      }}
    >
      {children}
    </div>
  )
}

Sidebar.Divider = function Divider({ children, variant = 'old' }) {
  const T = TOKENS[variant]

  return (
    <div
      style={{
        width: '100%',
        height: 1,
        backgroundColor: T.divider,
        marginTop: 22,
        marginBottom: 22,
      }}
    >
      {children}
    </div>
  )
}
Sidebar.Spacing = function Spacing({ children }) {
  return (
    <div
      style={{
        width: '100%',
        height: 24,
        backgroundColor: 'transparent',
      }}
    >
      {children}
    </div>
  )
}

Sidebar.Item = function Item({ id, children, onClick, variant = 'new' }) {
  const T = TOKENS[variant]
  return (
    <div
      onClick={onClick}
      style={{
        width: '100%',
        fontWeight: 500,
        fontSize: '16px',
        borderRadius: T.itemRadius,
        padding: '4px 0',
        cursor: onClick ? 'pointer' : 'default',
        color: T.itemText,
      }}
    >
      <div style={{ width: '100%' }}>{children}</div>
    </div>
  )
}

Sidebar.ActionLink = function ActionLink({ onClick, children, variant = 'old' }) {
  const T = TOKENS[variant]
  return (
    <Button
      onClick={onClick}
      noBg
      noPadding
      extend={{
        width: '100%',
        textAlign: 'left',
        marginTop: 12,
        paddingLeft: 12,
        color: T.link,
        textTransform: 'none',
        '&:hover': { backgroundColor: 'transparent' },
      }}
    >
      {children}
    </Button>
  )
}

/* ───────────────────────────── Styles ───────────────────────────── */

const styles = (showFilterBar, disturbanceBannerHeight) => ({
  sidebar_container: ({ theme: { layout } }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: `${layout.sidebarWidth}px`,
    height: '100vh',
    marginTop:
      layout.navBarHeight +
      (showFilterBar() ? layout.filterBarHeight : 0) +
      layout.navigationBarHeight +
      (disturbanceBannerHeight || 0),
  }),
  outer_sidebar: ({ theme: { layout, colors } }) => {
    const offset = layout.navBarHeight +
      (showFilterBar() ? layout.filterBarHeight : 0) +
      layout.navigationBarHeight +
      (disturbanceBannerHeight || 0)

    return {
      width: layout.sidebarWidth,
      display: 'flex',
      flexDirection: 'row',
      position: 'fixed',
      left: 0,
      top: offset,
      height: `calc(100vh - ${offset}px)`,
      bottom: 0,
      zIndex: 1,
      backgroundColor: colors.offWhite,
    }
  }
})

const railPanelStyles = (variant = 'old') => {
  const T = TOKENS[variant]
  return {
    rail: ({ theme: { layout, colors } }) => ({
      width: layout.sidebarItemWidth,
      height: '100%',
      backgroundColor: colors.offWhite,
      overflowX: 'hidden',
      overflowY: 'auto',
      borderRight: `1px solid ${T.panelBorder}`,
      '> a': {
        paddingTop: 8,
        transition: 'color 200ms ease-in-out',
      },
      '& [aria-label]': {
        borderBottom: `1px solid ${T.panelBorder}`,
      },
    }),
    panel: ({ theme: { layout } }) => ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: layout.sidebarWidth - layout.sidebarItemWidth + 'px',
      height: '100%',
      paddingTop: 0,
      paddingLeft: 16,
      paddingRight: 16,
      borderRight: `1px solid ${T.panelBorder}`,
      borderLeft: 'none',
      backgroundColor: T.panelBg,
      overflowY: 'auto',
    }),
  }
}